// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Alert } from '@fluentui/react-components/unstable';
import React from 'react';
import { AlertType } from '../../libs/models/AlertType';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { removeAlert } from '../../redux/features/app/appSlice';
import { Dismiss16 } from './BundledIcons';

const AUTO_DISMISS_MS = 5000;
// Errors stay until manually dismissed — only success/info/warning auto-dismiss
const AUTO_DISMISS_TYPES: AlertType[] = [AlertType.Success, AlertType.Info, AlertType.Warning];

const useClasses = makeStyles({
    alert: {
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground1,
        backgroundColor: tokens.colorNeutralBackground6,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
    },
    actionItems: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(tokens.spacingHorizontalMNudge),
    },
    button: {
        alignSelf: 'center',
    },
});

interface AlertItemProps {
    type: AlertType;
    message: string;
    onRetry?: () => void;
    index: number;
    onDismiss: (index: number) => void;
}

const AlertItem: React.FC<AlertItemProps> = ({ type, message, onRetry, index, onDismiss }) => {
    const classes = useClasses();

    React.useEffect(() => {
        if (!AUTO_DISMISS_TYPES.includes(type)) return;
        const timer = setTimeout(() => {
            onDismiss(index);
        }, AUTO_DISMISS_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [index, type, onDismiss]);

    return (
        <Alert
            intent={type}
            action={{
                children: (
                    <div className={classes.actionItems}>
                        {onRetry && <div onClick={onRetry}>Retry</div>}
                        <Dismiss16
                            aria-label="dismiss message"
                            onClick={() => {
                                onDismiss(index);
                            }}
                            color="black"
                            className={classes.button}
                        />
                    </div>
                ),
            }}
            key={`${index}-${type}`}
            className={classes.alert}
        >
            {message.slice(0, 1000) + (message.length > 1000 ? '...' : '')}
        </Alert>
    );
};

export const Alerts: React.FC = () => {
    const dispatch = useAppDispatch();
    const { alerts } = useAppSelector((state: RootState) => state.app);

    const handleDismiss = React.useCallback(
        (index: number) => {
            dispatch(removeAlert(index));
        },
        [dispatch],
    );

    return (
        <div>
            {alerts.map(({ type, message, onRetry }, index) => (
                <AlertItem
                    key={`${index}-${type}`}
                    type={type}
                    message={message}
                    onRetry={onRetry}
                    index={index}
                    onDismiss={handleDismiss}
                />
            ))}
        </div>
    );
};
